import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function AdminLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const navigate = useNavigate();
  const canSwitchToCustomer = typeof window !== "undefined"
    && ["localhost", "127.0.0.1"].includes(window.location.hostname)
    && Boolean(localStorage.getItem("biten_auth"));

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { username, password });
      localStorage.setItem("biten_admin", JSON.stringify(res.data));
      localStorage.setItem("biten_test_role", "admin");
      api.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
      toast.success("Välkommen till Bästa Biten");
      navigate("/manage");
    } catch (e) {
      if (e.response?.status === 429) {
        toast.error("För många försök. Vänta en minut och försök igen.");
        setCooldown(60);
      } else {
        toast.error(e.response?.data?.detail || "Felaktiga inloggningsuppgifter");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown(prev => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img src="/assets/logo/logo.jpg" alt="biten" className="h-16 w-16 object-cover mx-auto rounded-full shadow-sm mb-3" />
          <p className="text-[#D4D4D8] mt-2 text-sm">Restaurangpanel</p>
        </div>
        <form onSubmit={handleLogin} className="bg-[#121212] rounded-xl border border-[#27272A] p-6 space-y-4">
          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1">Användarnamn</Label>
            <Input value={username} onChange={e => setUsername(e.target.value)} placeholder="admin" className="bg-[#1A1A1A] border-[#27272A] text-[#EDEDED] h-11" data-testid="admin-username" required />
          </div>
          <div>
            <Label className="text-[#D4D4D8] text-xs mb-1">Lösenord</Label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Lösenord" className="bg-[#1A1A1A] border-[#27272A] text-[#EDEDED] h-11" data-testid="admin-password" required />
          </div>
          <Button type="submit" disabled={loading || cooldown > 0} className="w-full bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold h-11 disabled:opacity-50" data-testid="admin-login-btn">
            {cooldown > 0 ? `Vänta ${cooldown}s` : loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Logga in"}
          </Button>

          {canSwitchToCustomer && (
            <Button
              type="button"
              variant="outline"
              className="w-full border-[#27272A] text-[#EDEDED] hover:bg-[#1A1A1A]"
              onClick={() => {
                localStorage.setItem("biten_test_role", "customer");
                navigate("/");
              }}
            >
              Växla till kundvy
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
